import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentMultiEmploymentData,
  type EnrollmentMultiEmploymentSubmitInput,
  type EnrollmentMultiEmploymentSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Multi Employment data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiEmploymentData>
 * @example const response = await getEnrollmentMultiEmploymentData()
 */
export const getEnrollmentMultiEmploymentData = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiEmploymentData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiEmploymentData',
      query: `mutation getEnrollmentMultiEmploymentData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
              progress
              employmentStatus
              occupation
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiEmploymentData }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiEmploymentData = createQueryHook<undefined, EnrollmentMultiEmploymentData>(
  getEnrollmentMultiEmploymentData,
)

/**
 * Submit Enrollment Multi Employment data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiEmploymentSubmitOutput>
 * @example const response = await submitEnrollmentMultiEmploymentData();
 */
export const submitEnrollmentMultiEmploymentData = async (
  enrollmentEmploymentSubmitInput: EnrollmentMultiEmploymentSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiEmploymentSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiEmploymentData',
      query: `mutation submitEnrollmentMultiEmploymentData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentEmploymentSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiEmploymentSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiEmploymentData = createMutationHook<
  EnrollmentMultiEmploymentSubmitInput,
  EnrollmentMultiEmploymentSubmitOutput
>(submitEnrollmentMultiEmploymentData)
